import React, { useState, useMemo } from 'react';
import { CommonFilter } from './CommonFilter';
import { months } from '../../Utils/Utilities';
import { format } from "date-fns";

/**
 * PeriodFilter - A reusable component for date/period filtering
 * 
 * @param {Object} props
 * @param {Object} props.value - Current filter values { dateRange, otherMonth, otherQuarter, otherYear }
 * @param {Function} props.onChange - Callback when filter changes (receives updated filter object)
 * @param {Array} props.availableMonths - Optional custom month options (e.g., ["January 2026", "December 2025"])
 * @param {Array} props.availableQuarters - Optional custom quarter options (e.g., ["Q1 2026", "Q4 2025"])
 * @param {Array} props.availableYears - Optional custom year options (e.g., [{name: "2026", ID: "2026"}])
 * @param {Array} props.periodOptions - Optional custom period options (defaults to all standard options)
 * @param {Boolean} props.showMonthFilter - Show/hide month filter (default: true)
 * @param {Boolean} props.showQuarterFilter - Show/hide quarter filter (default: true)
 * @param {Boolean} props.showYearFilter - Show/hide year filter (default: true)
 * @param {Date} props.oldestDate - Oldest date for generating default options (default: current date)
 * @param {Object} props.monthlyData - Array of monthly data to extract available periods from
 * 
 * @example
 * // Basic usage with default options
 * <PeriodFilter
 *   value={filters}
 *   onChange={(newFilters) => setFilters(newFilters)}
 * />
 * 
 * @example
 * // With custom available months from data
 * <PeriodFilter
 *   value={filters}
 *   onChange={(newFilters) => setFilters(newFilters)}
 *   monthlyData={monthlyJson}
 * />
 * 
 * @example
 * // With custom period options
 * <PeriodFilter
 *   value={filters}
 *   onChange={(newFilters) => setFilters(newFilters)}
 *   periodOptions={["This Month", "Last Month", "Other Month"]}
 *   showQuarterFilter={false}
 *   showYearFilter={false}
 * />
 */
const PeriodFilter = ({
    value = {},
    onChange,
    availableMonths,
    availableQuarters,
    availableYears,
    periodOptions,
    showMonthFilter = true,
    showQuarterFilter = true,
    showYearFilter = true,
    showDateFilter = true,
    oldestDate = new Date(),
    monthlyData = null,
    isRecordsMode = false,
}) => {
    // Helper functions
    const getCurrentMonthYear = () => {
        const now = new Date();
        const monthName = months[now.getMonth()];
        return `${monthName} ${now.getFullYear()}`;
    };

    const getCurrentQuarterYear = () => {
        const now = new Date();
        const q = Math.ceil((now.getMonth() + 1) / 3);
        return `Q${q} ${now.getFullYear()}`;
    };

    const getCurrentYear = () => new Date().getFullYear().toString();

    const getTodayDateStr = () => {
        const now = new Date();
        return format(now, "yyyy-MM-dd");
    };

    const getCurrentDateLabel = () => {
        const now = new Date();
        return format(now, "d MMM");
    };

    // Extract available periods from monthlyData if provided
    const getAvailableMonthsFromData = (data) => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            return [getCurrentMonthYear()];
        }

        const uniqueMonths = new Set();
        data.forEach(item => {
            if (item.YY && item.MM) {
                const monthName = months[item.MM - 1];
                const monthYear = `${monthName} ${item.YY}`;
                uniqueMonths.add(monthYear);
            }
        });

        return Array.from(uniqueMonths).sort((a, b) => {
            const [aMonth, aYear] = a.split(' ');
            const [bMonth, bYear] = b.split(' ');
            const aDate = new Date(parseInt(aYear), months.indexOf(aMonth));
            const bDate = new Date(parseInt(bYear), months.indexOf(bMonth));
            return bDate - aDate;
        });
    };

    const getAvailableQuartersFromData = (data) => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            return [getCurrentQuarterYear()];
        }

        const uniqueQuarters = new Set();
        data.forEach(item => {
            if (item.YY && item.MM) {
                const quarter = Math.ceil(item.MM / 3);
                const quarterYear = `Q${quarter} ${item.YY}`;
                uniqueQuarters.add(quarterYear);
            }
        });

        return Array.from(uniqueQuarters).sort((a, b) => {
            const [aQ, aYear] = a.split(' ');
            const [bQ, bYear] = b.split(' ');
            const aQuarter = parseInt(aQ.replace('Q', ''));
            const bQuarter = parseInt(bQ.replace('Q', ''));
            const aDate = new Date(parseInt(aYear), (aQuarter - 1) * 3);
            const bDate = new Date(parseInt(bYear), (bQuarter - 1) * 3);
            return bDate - aDate;
        });
    };

    const getAvailableYearsFromData = (data) => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            return [{ name: getCurrentYear(), ID: getCurrentYear() }];
        }

        const uniqueYears = new Set();
        data.forEach(item => {
            if (item.YY) {
                uniqueYears.add(item.YY);
            }
        });

        return Array.from(uniqueYears)
            .sort((a, b) => b - a)
            .map(year => ({ name: year.toString(), ID: year.toString() }));
    };

    // Generate default past month options
    const getPastMonthOptions = (oldestDate) => {
        const options = [];
        const now = new Date();
        const oldest = new Date(oldestDate);
        let current = new Date(now.getFullYear(), now.getMonth(), 1);

        while (current >= oldest) {
            const monthName = months[current.getMonth()];
            const year = current.getFullYear();
            options.push(`${monthName} ${year}`);
            current.setMonth(current.getMonth() - 1);
        }

        return options;
    };

    // Generate default past quarter options
 // Generate default past quarter options
    const getPastQuarterOptions = (oldestDate) => {
        const options = [];
        const now = new Date();
        const oldest = new Date(oldestDate);
        
        // Normalize oldest to the start of its quarter to ensure it's included
        const oldestYear = oldest.getFullYear();
        const oldestQuarter = Math.ceil((oldest.getMonth() + 1) / 3);
        const oldestQuarterStart = new Date(oldestYear, (oldestQuarter - 1) * 3, 1);

        let currentYear = now.getFullYear();
        let currentQuarter = Math.ceil((now.getMonth() + 1) / 3);

        while (true) {
            const quarterStartMonth = (currentQuarter - 1) * 3;
            const loopQuarterDate = new Date(currentYear, quarterStartMonth, 1);

            // Stop only if the current loop date is strictly before the oldest quarter's start
            if (loopQuarterDate < oldestQuarterStart) break;

            options.push(`Q${currentQuarter} ${currentYear}`);

            currentQuarter--;
            if (currentQuarter === 0) {
                currentQuarter = 4;
                currentYear--;
            }
            
            // Safety break to prevent infinite loops if oldestDate is invalid
            if (currentYear < oldestYear - 1) break;
        }

        return options;
    };

    // Generate default year options
    const getYearOptions = (oldestDate) => {
        const options = [];
        const now = new Date();
        const oldest = new Date(oldestDate);
        const currentYear = now.getFullYear();
        const oldestYear = oldest.getFullYear();

        for (let year = currentYear; year >= oldestYear; year--) {
            options.push({ name: year.toString(), ID: year.toString() });
        }

        return options;
    };

    // Default period options - Only show these 6 options
    const defaultPeriodOptions = isRecordsMode ? [
        "Today",
        "Yesterday",
        "Other Date",
        "This Week",
        "Last Week",
        "This Month",
        "Other Month",
        "This Quarter",
        "Other Quarter",
        "This Year",
        "Other Year"
    ] : [
        "This Month",
        "Other Month",
        "This Quarter",
        "Other Quarter",
        "This Year",
        "Other Year"
    ];

    // Determine which options to use
    const finalPeriodOptions = periodOptions || defaultPeriodOptions;

    const finalMonthOptions = useMemo(() => {
        let options = [];
        if (availableMonths) {
            options = availableMonths;
        } else if (monthlyData) {
            options = getAvailableMonthsFromData(monthlyData);
        } else {
            options = getPastMonthOptions(oldestDate);
        }
        // Exclude current month from the list since it's the default
       return options;
    }, [availableMonths, monthlyData, oldestDate]);

    const finalQuarterOptions = useMemo(() => {
        let options = [];
        if (availableQuarters) {
            options = availableQuarters;
        } else if (monthlyData) {
            options = getAvailableQuartersFromData(monthlyData);
        } else {
            options = getPastQuarterOptions(oldestDate);
        }
        // Exclude current quarter from the list since it's the default
       return options;
    }, [availableQuarters, monthlyData, oldestDate]);

    const finalYearOptions = useMemo(() => {
        let options = [];
        if (availableYears) {
            options = availableYears;
        } else if (monthlyData) {
            options = getAvailableYearsFromData(monthlyData);
        } else {
            options = getYearOptions(oldestDate);
        }
        // Exclude current year from the list since it's the default
       return options;
    }, [availableYears, monthlyData, oldestDate]);

    // Generate date options for current month (Records mode only)
    const finalDateOptions = useMemo(() => {
        if (!isRecordsMode) return [];

        const result = [];
        const today = new Date();
        const startOfMonthDate = new Date(today.getFullYear(), today.getMonth(), 1);
        let d = new Date(startOfMonthDate);

        while (d <= today) {
            const formattedValue = format(d, "yyyy-MM-dd");
            const label = format(d, "d MMM");
            result.push({ name: label, ID: formattedValue });
            d.setDate(d.getDate() + 1);
        }

        return result.reverse();
    }, [isRecordsMode]);

    // Handle filter changes
    const handlePeriodChange = (newPeriod) => {
        const updatedFilters = {
            ...value,
            dateRange: newPeriod,
        };

        // ALWAYS reset to current defaults when switching to a period type
        // This ensures clean state - user doesn't see old selections
        if (newPeriod === "Other Month") {
            updatedFilters.otherMonth = getCurrentMonthYear();
        }
        if (newPeriod === "Other Quarter") {
            updatedFilters.otherQuarter = getCurrentQuarterYear();
        }
        if (newPeriod === "Other Year") {
            updatedFilters.otherYear = getCurrentYear();
        }
        if (newPeriod === "Other Date" && isRecordsMode) {
            updatedFilters.otherDate = getTodayDateStr();
        }

        onChange(updatedFilters);
    };


    const handleSubFilterChange = (key, newValue) => {
        // Prevent NULL/empty values - always ensure a default
        let finalValue = newValue;

        if (!newValue || newValue === "all" || newValue === "") {
            if (key === "otherMonth") {
                finalValue = getCurrentMonthYear();
            } else if (key === "otherQuarter") {
                finalValue = getCurrentQuarterYear();
            } else if (key === "otherYear") {
                finalValue = getCurrentYear();
            } else if (key === "otherDate" && isRecordsMode) {
                finalValue = getTodayDateStr();
            }
        }

        onChange({
            ...value,
            [key]: finalValue,
        });
    };

    // Helper to check if clear button should be shown
    const shouldShowClearButton = (filterType, currentValue) => {
        if (filterType === "otherMonth") {
            return currentValue !== getCurrentMonthYear();
        } else if (filterType === "otherQuarter") {
            return currentValue !== getCurrentQuarterYear();
        } else if (filterType === "otherYear") {
            return currentValue !== getCurrentYear();
        } else if (filterType === "otherDate" && isRecordsMode) {
            return currentValue !== getTodayDateStr();
        }
        return false;
    };


    return (
        <>
            {/* Main Period Filter */}
            <CommonFilter
                key="dateRange"
                label={isRecordsMode ? "Today" : "This Month"}
                options={isRecordsMode
                    ? finalPeriodOptions.filter(item => item !== "Today")
                    : finalPeriodOptions.filter(item => item !== "This Month")
                }
                value={value.dateRange || (isRecordsMode ? "Today" : "This Month")}
                onChange={handlePeriodChange}
                searchEnabled={false}
                type="options"
                mobileLabel="Period"
                showAllOption={false}
            />

            {/* Other Month Filter */}
            {showMonthFilter && (value.dateRange === "Other Month") && (
                <CommonFilter
                    key="otherMonth"
                    label="Select Month"
                    options={finalMonthOptions}
                    value={value.otherMonth || getCurrentMonthYear()}
                    onChange={(val) => handleSubFilterChange('otherMonth', val)}
                    displayValue={(val) => val || getCurrentMonthYear()}
                    type="list"
                    mobileDropdownText="Select Month"
                    mobileLabel="Month"
                    showAllOption={false}
                    hideLabelOption={true}
                    isSorting={false}
                    showClearButton={shouldShowClearButton('otherMonth', value.otherMonth)}
                />
            )}

            {/* Other Quarter Filter */}
            {showQuarterFilter && (value.dateRange === "Other Quarter") && (
                <CommonFilter
                    key="otherQuarter"
                    options={finalQuarterOptions}
                    value={value.otherQuarter || getCurrentQuarterYear()}
                    onChange={(val) => handleSubFilterChange('otherQuarter', val)}
                    displayValue={(val) => val || getCurrentQuarterYear()}
                    type="list"
                    mobileDropdownText="Select Quarter"
                    mobileLabel="Quarter"
                    showAllOption={false}
                    hideLabelOption={true}
                    isSorting={false}
                    showClearButton={shouldShowClearButton('otherQuarter', value.otherQuarter)}
                />
            )}

            {/* Other Year Filter */}
            {showYearFilter && (value.dateRange === "Other Year") && (
                <CommonFilter
                    key="otherYear"
                    label="Select Year"
                    options={finalYearOptions}
                    value={value.otherYear || getCurrentYear()}
                    onChange={(val) => handleSubFilterChange('otherYear', val)}
                    displayValue={(val) => val || getCurrentYear()}
                    type="list"
                    mobileDropdownText="Select Year"
                    mobileLabel="Year"
                    showAllOption={false}
                    hideLabelOption={true}
                    isSorting={false}
                    showClearButton={shouldShowClearButton('otherYear', value.otherYear)}
                />
            )}

            {/* Other Date Filter - Records Mode Only */}
            {isRecordsMode && showDateFilter && (value.dateRange === "Other Date") && (
                <CommonFilter
                    key="otherDate"
                    label="Select Date"
                    options={finalDateOptions}
                    value={value.otherDate || getTodayDateStr()}
                    onChange={(val) => handleSubFilterChange('otherDate', val)}
                    displayValue={(val) => {
                        if (!val || val === "all") return getCurrentDateLabel();
                        try {
                            return format(new Date(val), "d MMM");
                        } catch {
                            return getCurrentDateLabel();
                        }
                    }}
                    type="list"
                    mobileDropdownText="Select Date"
                    mobileLabel="Date"
                    showAllOption={false}
                    hideLabelOption={true}
                    isSorting={false}
                    showClearButton={false}
                    searchEnabled={true}
                />
            )}
        </>
    );
};

export default PeriodFilter;
